import {
    Platform
} from "react-native";
import type {
    SliderFractionConstantType,
    SliderPointerConstantType,
    SliderOffsetConstantType,
    SliderOriginConstantType,
    SliderLabelsConstantType,
    SliderStopsConstantType,
    SliderTicksConstantType,
    SliderValueConstantType,
    SliderSnapConstantType,
    SliderMoveConstantType,
    SliderUnitConstantType,
    SliderTextConstantType,
    SliderKeyConstantType,
    SliderTipConstantType,
    SliderRangeValue,
    SliderKeyAction,
    SliderLabel,
    SliderMark
} from "./type";
import {
    getHorizontalStep
} from "../../helpers/direction";
import {
    formatNumber
} from "../../helpers/locale";

export const SLIDER_TIP_CHAR_RATIO = 0.6;

export const SLIDER_PAGE_STEPS = 10;

export const SLIDER_PRECISION_DIGITS = 6;

const getSliderDecimals = (value: number): number => {
    const text = String(value);

    if(text.includes("e-")) {
        return Number(text.split("e-")[1]);
    }

    const [
        ,
        fraction = ""
    ] = text.split(".");

    return fraction.length;
};

export const clampSliderValue = ({
    value,
    min,
    max
}: SliderFractionConstantType): number => {
    return Math.min(Math.max(value, min), max);
};

export const fixSliderPrecision = ({
    value,
    step,
    min
}: Omit<SliderSnapConstantType, "max" | "stops">): number => {
    return Number(value.toFixed(Math.min(Math.max(getSliderDecimals(step), getSliderDecimals(min)), SLIDER_PRECISION_DIGITS)));
};

export const getSliderStops = ({
    marks,
    step
}: SliderStopsConstantType): Array<number> | undefined => {
    if(step !== undefined || !marks?.length) {
        return undefined;
    }

    return Array.from(new Set(marks.map((mark) => mark.value))).sort((first, second) => first - second);
};

export const getSliderUnit = ({
    stops,
    step
}: SliderUnitConstantType): number => {
    if(!stops || stops.length < 2) {
        return step;
    }

    return stops.reduce((unit, stop, index) => {
        if(!index) {
            return unit;
        }

        return Math.min(unit, stop - (stops[index - 1] ?? stop));
    }, Infinity);
};

export const snapSliderValue = ({
    stops,
    value,
    step,
    min,
    max
}: SliderSnapConstantType): number => {
    const clamped = clampSliderValue({
        value,
        min,
        max
    });

    if(stops?.length) {
        return clampSliderValue({
            value: stops.reduce((nearest, stop) => Math.abs(stop - clamped) < Math.abs(nearest - clamped) ? stop : nearest, stops[0] ?? clamped),
            min,
            max
        });
    }

    if(step <= 0) {
        return clamped;
    }

    return clampSliderValue({
        value: fixSliderPrecision({
            value: min + Math.round((clamped - min) / step) * step,
            step,
            min
        }),
        min,
        max
    });
};

export const moveSliderValue = ({
    count,
    stops,
    value,
    step,
    min,
    max
}: SliderMoveConstantType): number => {
    if(stops?.length) {
        const nearestIndex = stops.reduce((nearest, stop, index) => Math.abs(stop - value) < Math.abs((stops[nearest] ?? value) - value) ? index : nearest, 0);

        return stops[Math.min(Math.max(nearestIndex + count, 0), stops.length - 1)] ?? value;
    }

    return snapSliderValue({
        value: value + count * step,
        step,
        min,
        max
    });
};

export const getSliderFraction = ({
    value,
    min,
    max
}: SliderFractionConstantType): number => {
    if(max <= min) {
        return 0;
    }

    return clampSliderValue({
        value: (value - min) / (max - min),
        max: 1,
        min: 0
    });
};

export const getSliderValues = ({
    value,
    min,
    max
}: SliderValueConstantType): SliderRangeValue => {
    if(Array.isArray(value)) {
        const [
            first,
            second
        ] = value.map((item) => clampSliderValue({
            value: item,
            min,
            max
        })).sort((firstItem, secondItem) => firstItem - secondItem);

        return [
            first ?? min,
            second ?? max
        ];
    }

    return [
        min,
        clampSliderValue({
            value,
            min,
            max
        })
    ];
};

export const getSliderOffsetFraction = ({
    thumbSize,
    length,
    offset
}: SliderOffsetConstantType): number => {
    const usable = length - thumbSize;

    if(usable <= 0) {
        return 0;
    }

    return clampSliderValue({
        value: (offset - thumbSize / 2) / usable,
        max: 1,
        min: 0
    });
};

export const getSliderOrigin = ({
    orientation,
    direction,
    height,
    pageX,
    pageY,
    width
}: SliderOriginConstantType): number => {
    const isWeb = Platform.OS === "web" && typeof window !== "undefined";

    const scrollX = isWeb ? window.scrollX : 0;

    const scrollY = isWeb ? window.scrollY : 0;

    if(orientation === "vertical") {
        return pageY + scrollY + height;
    }

    return (direction === "rtl" ? pageX + width : pageX) + scrollX;
};

export const getSliderPointerOffset = ({
    orientation,
    direction,
    pointer,
    origin
}: SliderPointerConstantType): number => {
    if(orientation === "horizontal" && direction === "ltr") {
        return pointer - origin;
    }

    return origin - pointer;
};

export const getSliderKeyAction = ({
    direction,
    shiftKey,
    key
}: SliderKeyConstantType): SliderKeyAction => {
    const pageSteps = shiftKey ? SLIDER_PAGE_STEPS : 1;

    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    if(horizontalStep) {
        return horizontalStep * pageSteps;
    }

    if(key === "ArrowUp") {
        return pageSteps;
    }

    if(key === "ArrowDown") {
        return -pageSteps;
    }

    if(key === "PageUp") {
        return SLIDER_PAGE_STEPS;
    }

    if(key === "PageDown") {
        return -SLIDER_PAGE_STEPS;
    }

    if(key === "Home") {
        return "min";
    }

    if(key === "End") {
        return "max";
    }

    return null;
};

export const getSliderTicks = ({
    minSpacing,
    usable,
    stops,
    step,
    min,
    max
}: SliderTicksConstantType): Array<number> => {
    const count = stops ? stops.length : Math.floor(Number(((max - min) / step).toFixed(SLIDER_PRECISION_DIGITS))) + 1;

    if(count < 2 || step <= 0 && !stops || usable / (count - 1) < minSpacing) {
        return [];
    }

    if(stops) {
        return stops;
    }

    return Array.from({
        length: count
    }, (_, index) => fixSliderPrecision({
        value: min + index * step,
        step,
        min
    }));
};

export const getSliderValueText = ({
    formatValueText,
    locale,
    marks,
    value
}: SliderTextConstantType): string => {
    if(formatValueText) {
        return formatValueText(value);
    }

    const markTitle = marks?.find((mark) => mark.value === value && mark.title)?.title;

    if(markTitle) {
        return markTitle;
    }

    return formatNumber({
        locale,
        value
    });
};

export const getSliderLabels = ({
    isShowMinMaxLabels,
    formatText,
    marks,
    min,
    max
}: SliderLabelsConstantType): Array<SliderLabel> => {
    const entries: Array<SliderMark> = (marks ?? []).filter((mark) => mark.title);

    if(isShowMinMaxLabels) {
        [
            min,
            max
        ].forEach((value) => {
            if(!entries.some((entry) => entry.value === value)) {
                entries.push({
                    title: formatText(value),
                    value
                });
            }
        });
    }

    return entries.sort((first, second) => first.value - second.value).map((entry) => ({
        fraction: getSliderFraction({
            value: entry.value,
            min,
            max
        }),
        align: getSliderLabelAlign({
            value: entry.value,
            min,
            max
        }),
        text: entry.title ?? ""
    }));
};

const getSliderLabelAlign = ({
    value,
    min,
    max
}: SliderFractionConstantType): SliderLabel["align"] => {
    if(max <= min) {
        return "start";
    }

    if(value <= min) {
        return "start";
    }

    if(value >= max) {
        return "end";
    }

    return "center";
};

export const getIsSliderTipOverlap = ({
    labelVariant,
    typography,
    distance,
    padding,
    texts
}: SliderTipConstantType): boolean => {
    const fontSize = typography[labelVariant].fontSize;

    const widths = texts.map((text) => text.length * fontSize * SLIDER_TIP_CHAR_RATIO + padding * 2);

    return distance < ((widths[0] ?? 0) + (widths[1] ?? 0)) / 2;
};
